import React, { useState, useEffect } from 'react';
import { CollaboratorAvatar, TextInput, Button, Toast } from 'components';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { updateGroupMutation } from 'services/apollo/mutations';
import classnames from 'classnames';
import { Logger, groupNotFoundError } from 'services';
import { GroupSummary } from './GroupSummary';
import styles from './GroupHero.module.scss';
import { Modal, DefaultModalContent } from 'components';
import { useTranslation } from 'hooks/useTranslation';
import { useRemoveGroupMutation, useRemoveGroupMemberMutation } from 'services/apollo/hooks';
import Icon from 'assets/icons/';
import gql from 'graphql-tag';
import { toast } from 'react-toastify';

const GroupHeroQuery = gql`
  query getGroupHero($groupId: String!) {
    me {
      userProfile {
        id
      }
    }
    groupById(id: $groupId) {
      id
      name
      emoji
      isAdmin
    }
  }
`;

interface GroupHeroProps {
  className?: string;
  showPinned?: boolean;
  togglePinned?: () => void;
}

export const GroupHero: React.FC<GroupHeroProps> = ({ className, showPinned, togglePinned }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId } = useParams();
  const [updateGroup] = useMutation(updateGroupMutation);
  const [removeGroup] = useRemoveGroupMutation(groupId);
  const [currentTitle, setCurrentTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [deleteGroupModalIsOpen, setDeleteGroupModalIsOpen] = React.useState(false);
  const [leaveGroupModalIsOpen, setLeaveGroupModalIsOpen] = React.useState(false);

  // When viewing another group, stop editing
  useEffect(() => {
    setIsEditing(false);
  }, [groupId]);

  const { error, data } = useQuery(GroupHeroQuery, {
    variables: { groupId },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const isAdmin = data?.groupById?.isAdmin;
  const [removeGroupMember] = useRemoveGroupMemberMutation(
    groupId,
    data?.me?.userProfile?.id,
    isAdmin
  );

  if (data === undefined) return <GroupHeroLoader />;

  // Group object doesn't return a result, so redirect to home
  if (data !== undefined && data?.groupById == null) {
    toast(<Toast message={t('toasts.group-not-found')} hasCloseButton />, {
      position: toast.POSITION.BOTTOM_LEFT,
      hideProgressBar: true,
      closeButton: false,
      closeOnClick: true,
      autoClose: 5000,
    });
    history.push(`/`);
    return <GroupHeroLoader />;
  }

  if (error) {
    return <GroupHeroLoader />;
  }

  if (data.errors) {
    if (groupNotFoundError(error)) {
      toast(<Toast message={t('toasts.group-not-found')} hasCloseButton />, {
        position: toast.POSITION.BOTTOM_LEFT,
        hideProgressBar: true,
        closeButton: false,
        closeOnClick: true,
        autoClose: 5000,
      });
      history.push(`/`);
    }
  }

  // Edit Group Name

  const startEditing = () => {
    setCurrentTitle(data?.groupById?.name);
  };

  const handleTitleUpdate = () => {
    updateGroup({
      variables: { id: groupId, name: currentTitle },
      optimisticResponse: {
        __typename: 'Mutation',
        updateGroup: {
          __typename: 'UpdateGroup',
          group: {
            __typename: 'Group',
            id: groupId,
            name: currentTitle,
          },
        },
      },
    });
    setIsEditing(false);
  };

  // Delete Modal

  const handleDeleteGroup = () => {
    removeGroup().then((data: any) => {
      setDeleteGroupModalIsOpen(false);

      if (data?.data?.removeGroup?.error) {
        Logger.log(error);

        toast(<Toast message={t('toasts.delete-group-error')} hasCloseButton />, {
          position: toast.POSITION.BOTTOM_LEFT,
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: false,
          autoClose: false,
        });
      }

      if (data?.data?.removeGroup?.ok) {
        history.push('/');
      }
    });
  };

  // Leave Modal

  const handleLeaveGroup = () => {
    removeGroupMember().then((data: any) => {
      setLeaveGroupModalIsOpen(false);

      if (data?.data?.removeGroupMember?.error) {
        Logger.log(error);

        toast(<Toast message={t('toasts.leave-group-error')} hasCloseButton />, {
          position: toast.POSITION.BOTTOM_LEFT,
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: false,
          autoClose: false,
        });
      }

      if (data?.data?.removeGroupMember?.ok) {
        history.push('/');
      }
    });
  };

  return (
    <>
      <div className={classnames(className, styles.container, isEditing && styles.isEditing)}>
        {!isEditing && (
          <div className={styles.edit}>
            <div
              className={classnames('hasTooltip tooltipBelowRight', styles.icon)}
              data-tooltip="Edit Group"
              onClick={() => {
                startEditing();
                setIsEditing(true);
              }}
            >
              <Icon.Edit />
            </div>
          </div>
        )}

        <CollaboratorAvatar
          bordered={false}
          type="Group"
          size="Enormous"
          emoji={data.groupById.emoji}
          collaborator={{
            displayName: data.groupById.name,
          }}
        />

        <div className={styles.meta}>
          {isEditing ? (
            <>
              {isAdmin ? (
                <>
                  <div className={classnames(styles.name, styles.editContainer)}>
                    {/* 
                //@ts-ignore */}
                    <TextInput
                      autoFocus
                      className={styles.input}
                      theme="Word"
                      value={currentTitle}
                      onChange={(title: any) => {
                        setCurrentTitle(title);
                      }}
                      placeholder={'placeholder'}
                    />
                    <div className={styles.buttonGroup}>
                      <Button
                        size="Tiny"
                        type="Cancel"
                        onClick={() => {
                          setIsEditing(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="Tiny"
                        onClick={() => {
                          handleTitleUpdate();
                          setIsEditing(false);
                        }}
                      >
                        Save changes
                      </Button>
                    </div>
                  </div>
                  <div className={styles.actionContainer}>
                    <Button
                      className={classnames('hasTooltip tooltipAboveRight', styles.actionButton)}
                      tooltipText="Delete Group"
                      type="Plain"
                      onClick={() => {
                        setDeleteGroupModalIsOpen(true);
                      }}
                    >
                      <Icon.Trash />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.name}>{data.groupById.name}</div>
                  <div className={styles.actionContainer}>
                    <div className={styles.buttonGroup}>
                      <Button
                        size="Tiny"
                        type="Cancel"
                        onClick={() => {
                          setIsEditing(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className={classnames('hasTooltip tooltipAboveRight', styles.actionButton)}
                        tooltipText="Leave Group"
                        type="Plain"
                        onClick={() => {
                          setLeaveGroupModalIsOpen(true);
                        }}
                      >
                        <Icon.ArrowReturn />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className={styles.name}>{data.groupById.name}</div>
          )}
          <div className={styles.collabDocs}>
            <div className={styles.documents}>
              <GroupSummary togglePinned={togglePinned} showPinned={showPinned} />
            </div>
          </div>
        </div>
      </div>
      {/* Delete Group Modal*/}
      <Modal
        isOpen={deleteGroupModalIsOpen}
        onRequestClose={() => {
          setDeleteGroupModalIsOpen(false);
        }}
      >
        <DefaultModalContent
          title={t('group-screen.delete-group-modal.title')}
          subtext={t('group-screen.delete-group-modal.subtext')}
          cancelButtonText={t('group-screen.delete-group-modal.cancelButtonText')}
          onCancel={() => {
            setDeleteGroupModalIsOpen(false);
          }}
          actionButtonText={t('group-screen.delete-group-modal.actionButtonText')}
          onAction={() => {
            handleDeleteGroup();
          }}
        />
      </Modal>
      {/* Leave Group Modal*/}
      <Modal
        isOpen={leaveGroupModalIsOpen}
        onRequestClose={() => {
          setLeaveGroupModalIsOpen(false);
        }}
      >
        <DefaultModalContent
          title={t('group-screen.leave-group-modal.title')}
          subtext={t('group-screen.leave-group-modal.subtext')}
          cancelButtonText={t('group-screen.leave-group-modal.cancelButtonText')}
          onCancel={() => {
            setLeaveGroupModalIsOpen(false);
          }}
          actionButtonText={t('group-screen.leave-group-modal.actionButtonText')}
          onAction={() => {
            handleLeaveGroup();
          }}
        />
      </Modal>
    </>
  );
};

export const GroupHeroLoader = () => {
  return <div className={styles.groupHeroLoader} />;
};
